// src/api/groupes.api.ts — SCE Issues
// Multi-équipements (Lot 14) : les écritures qui touchent PLUSIEURS fiches d'un
// groupe. Règles pures : utils/groupes.ts.
//
// ─── Une fiche sœur à la fois, relue avant d'écrire ───
//
// La liste des problèmes ne porte pas l'identifiant SharePoint des fiches, et
// son état peut dater : chaque sœur est RELUE (getIssueById) juste avant
// d'écrire, et n'est touchée que si elle est encore éligible — une sœur
// qualifiée à part entre-temps garde sa qualification. Une sœur en échec
// n'arrête pas les autres : le bilan la nomme, l'action se refait depuis sa
// fiche. L'événement d'activité de chaque sœur est best-effort, comme sur la
// fiche (cf. `tracer` dans IssueDetailPage).

import { addCommentaire } from './commentaires.api'
import { deduireCategorieEquipement } from './equipements.api'
import { createIssue, getIssueById, qualifyIssue, updateIssue } from './issues.api'
import { coordinateursDuProbleme } from '@/config/sujets.config'
import { A_QUALIFIER, estActif, type Equipement, type Issue } from '@/types/issue.types'
import type { BilanGroupe } from '@/utils/groupes'

type Evenement = Parameters<typeof addCommentaire>[0]

/**
 * Applique une action à chaque fiche sœur, l'une après l'autre.
 *
 * @param agir écrit sur la fiche et renvoie l'événement d'activité à poser
 *             (ou null pour n'en poser aucun).
 */
export async function appliquerAuxMembres(
  ids: readonly string[],
  eligible: (i: Issue) => boolean,
  agir: (itemId: string, issue: Issue) => Promise<Evenement | null>,
): Promise<BilanGroupe> {
  const bilan: BilanGroupe = { faits: [], ignores: [], echecs: [] }
  for (const id of ids) {
    try {
      const relu = await getIssueById(id)
      if (!relu) {
        bilan.echecs.push(id)
        continue
      }
      if (!eligible(relu.issue)) {
        bilan.ignores.push(id)
        continue
      }
      const evenement = await agir(relu.itemId, relu.issue)
      bilan.faits.push(id)
      if (evenement) {
        try {
          await addCommentaire(evenement)
        } catch (err) {
          console.warn(`[groupes] événement non tracé sur ${id} :`, err)
        }
      }
    } catch (err) {
      console.warn(`[groupes] action refusée sur ${id} :`, err)
      bilan.echecs.push(id)
    }
  }
  return bilan
}

/**
 * Ajoute une machine au groupe d'un problème (« Oui, par le coordinateur »).
 *
 * La nouvelle fiche reprend titre, description, métier et phase ; si le
 * problème d'origine est déjà qualifié et actif, elle l'est aussi (même
 * famille, même criticité) et passe en cours. Une fiche seule devient la
 * première de son groupe.
 *
 * @param auteur nom du compte RÉEL (il signe l'événement de création).
 * @returns la nouvelle fiche.
 * @throws Error si le N° de série n'a pas de suffixe connu, ou si la machine
 *         est déjà celle du problème.
 */
export async function ajouterMachine(
  source: Issue,
  sourceItemId: string,
  equipement: Pick<Equipement, 'numeroSerie' | 'of' | 'pn'>,
  auteur: { upn: string; nom: string },
): Promise<Issue> {
  const categorie = deduireCategorieEquipement(equipement.numeroSerie)
  if (!categorie) {
    throw new Error(`N° de série ${equipement.numeroSerie} : suffixe inconnu, équipement non ajouté.`)
  }
  if (equipement.numeroSerie.trim().toLowerCase() === source.numeroSerie.trim().toLowerCase()) {
    throw new Error('Cet équipement est déjà celui du problème.')
  }

  const groupe = source.groupe ?? source.id
  if (!source.groupe) await updateIssue(sourceItemId, { groupe })

  const cree = await createIssue(
    {
      titre: source.titre,
      metier: source.metier,
      ...(source.sujet ? { sujet: source.sujet } : {}),
      phase: source.phase,
      numeroSerie: equipement.numeroSerie,
      of: equipement.of,
      pn: equipement.pn,
      familleEquipement: categorie,
      description: source.description,
      groupe,
    },
    auteur,
    coordinateursDuProbleme(source).map((c) => c.upn),
  )
  try {
    await addCommentaire({
      problemId: cree.id,
      type: 'Creation',
      contenu: `Équipement ajouté par ${auteur.nom} au défaut ${groupe} (depuis ${source.id})`,
      nouveauStatut: 'Ouvert',
    })
  } catch (err) {
    console.warn('[groupes] événement de création non tracé :', err)
  }

  const qualifie = source.famille !== A_QUALIFIER && source.criticite !== A_QUALIFIER
  if (!qualifie || !estActif(source)) return cree

  const relu = await getIssueById(cree.id)
  if (!relu) return cree
  const { issue } = await qualifyIssue(
    relu.itemId,
    { famille: source.famille, criticite: source.criticite },
    'Ouvert',
  )
  try {
    await addCommentaire({
      problemId: cree.id,
      type: 'Qualification',
      contenu: `Problème qualifié comme ${source.id} : famille « ${source.famille} » · criticité « ${source.criticite} »`,
      ancienStatut: 'Ouvert',
      nouveauStatut: 'En cours',
    })
  } catch (err) {
    console.warn('[groupes] événement de qualification non tracé :', err)
  }
  return issue
}
